import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  User,
  AlertTriangle,
  MessageSquare,
  Check,
  X,
  Send,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  FileText,
  Clock,
  Shield,
  Loader2,
  Eye,
  RefreshCw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// DS-160 Field Labels
const FIELD_LABELS = {
  surname: "Apellido(s)",
  given_name: "Nombre(s)",
  full_name_native: "Nombre en alfabeto nativo",
  has_other_names: "¿Ha usado otros nombres?",
  other_names: "Otros nombres usados",
  sex: "Sexo",
  marital_status: "Estado Civil",
  birth_date: "Fecha de Nacimiento",
  birth_city: "Ciudad de Nacimiento",
  birth_state: "Estado/Provincia de Nacimiento",
  birth_country: "País de Nacimiento",
  nationality: "Nacionalidad",
  has_other_nationality: "¿Tiene otra nacionalidad?",
  other_nationality: "Otra nacionalidad",
  has_permanent_resident: "¿Residente permanente de otro país?",
  permanent_resident_country: "País de residencia permanente",
  national_id: "ID Nacional (CURP, DNI, etc.)",
  us_ssn: "Número de Seguro Social EE.UU.",
  us_tax_id: "ITIN (Número fiscal EE.UU.)",
  home_address: "Dirección",
  home_city: "Ciudad",
  home_state: "Estado/Provincia",
  home_postal_code: "Código Postal",
  home_country: "País",
  mailing_address_same: "¿Dirección de correspondencia igual?",
  mailing_address: "Dirección de Correspondencia",
  phone_primary: "Teléfono Principal",
  phone_secondary: "Teléfono Secundario",
  phone_work: "Teléfono de Trabajo",
  email: "Correo Electrónico",
  passport_type: "Tipo de Pasaporte",
  passport_number: "Número de Pasaporte",
  passport_book_number: "Número de Libro de Pasaporte",
  passport_country: "País de Expedición",
  passport_city_issued: "Ciudad de Expedición",
  passport_state_issued: "Estado de Expedición",
  passport_issue_date: "Fecha de Expedición",
  passport_expiry_date: "Fecha de Vencimiento",
  has_lost_passport: "¿Ha perdido un pasaporte?",
  lost_passport_details: "Detalles del pasaporte perdido",
  travel_purpose: "Propósito del Viaje",
  travel_specific_plans: "¿Tiene planes específicos?",
  arrival_date: "Fecha de Llegada",
  departure_date: "Fecha de Salida",
  arrival_city: "Ciudad de Llegada",
  stay_address: "Dirección de Hospedaje",
  who_pays_trip: "Quién Paga el Viaje",
  payer_details: "Datos de quien paga",
  has_previous_us_travel: "Viajes Previos a EE.UU.",
  previous_travel_dates: "Fechas de viajes anteriores",
  us_drivers_license: "¿Licencia de conducir EE.UU.?",
  us_drivers_license_details: "Detalles de licencia",
  has_previous_visa: "Visa Anterior",
  previous_visa_number: "Número de visa anterior",
  previous_visa_issue_date: "Fecha de visa anterior",
  visa_same_type: "¿Mismo tipo de visa?",
  visa_same_country: "¿Mismo país de solicitud?",
  visa_was_refused: "Visa Negada",
  visa_refusal_details: "Detalles de negación",
  visa_was_cancelled: "Visa Cancelada",
  visa_cancellation_details: "Detalles de cancelación",
  us_contact_name: "Contacto en EE.UU. - Nombre",
  us_contact_organization: "Contacto - Organización",
  us_contact_relationship: "Relación con el contacto",
  us_contact_address: "Contacto en EE.UU. - Dirección",
  us_contact_city: "Contacto en EE.UU. - Ciudad",
  us_contact_state: "Contacto en EE.UU. - Estado",
  us_contact_zip: "Contacto - Código Postal",
  us_contact_phone: "Contacto - Teléfono",
  us_contact_email: "Contacto - Email",
  father_surname: "Apellido del Padre",
  father_given_name: "Nombre del Padre",
  father_birth_date: "Fecha de Nacimiento del Padre",
  father_in_us: "¿Padre en EE.UU.?",
  father_us_status: "Estatus del Padre en EE.UU.",
  mother_surname: "Apellido de la Madre",
  mother_given_name: "Nombre de la Madre",
  mother_birth_date: "Fecha de Nacimiento de la Madre",
  mother_in_us: "¿Madre en EE.UU.?",
  mother_us_status: "Estatus de la Madre en EE.UU.",
  has_relatives_in_us: "¿Familiares en EE.UU.?",
  relatives_in_us_details: "Detalles de familiares en EE.UU.",
  spouse_surname: "Apellido del Cónyuge",
  spouse_given_name: "Nombre del Cónyuge",
  spouse_birth_date: "Fecha de Nacimiento del Cónyuge",
  spouse_nationality: "Nacionalidad del Cónyuge",
  occupation: "Ocupación",
  employer_name: "Nombre del Empleador",
  employer_address: "Dirección del Empleador",
  employer_city: "Ciudad del Empleador",
  employer_phone: "Teléfono del Empleador",
  job_title: "Puesto/Cargo",
  job_start_date: "Fecha de Inicio",
  monthly_salary: "Salario Mensual",
  job_description: "Descripción del Trabajo",
  previous_employer: "¿Empleos anteriores?",
  previous_employment_details: "Detalles de empleos anteriores",
  education_level: "Nivel de Educación",
  school_name: "Nombre de la Institución",
  school_address: "Dirección de la Institución",
  course_of_study: "Carrera/Área de Estudio",
  education_start_date: "Fecha de Inicio de Estudios",
  education_end_date: "Fecha de Fin de Estudios",
  security_disease: "Enfermedad Contagiosa",
  security_disorder: "Desorden Mental",
  security_drug_addict: "Adicción a Drogas",
  security_arrested: "Arrestado",
  security_arrest_details: "Detalles del arresto",
  security_controlled_substances: "Sustancias Controladas",
  security_prostitution: "Prostitución",
  security_money_laundering: "Lavado de Dinero",
  security_trafficking: "Tráfico de Personas",
  security_terrorist: "Actividad Terrorista",
  security_genocide: "Genocidio/Tortura",
  security_child_custody: "Custodia de menores",
  security_voting: "Votación ilegal en EE.UU.",
  security_tax_evasion: "Evasión fiscal"
};

// Field sections for grouping
const FIELD_SECTIONS = {
  "Información Personal": ["surname", "given_name", "full_name_native", "has_other_names", "other_names", "sex", "marital_status", "birth_date", "birth_city", "birth_state", "birth_country"],
  "Nacionalidad": ["nationality", "has_other_nationality", "other_nationality", "has_permanent_resident", "permanent_resident_country", "national_id", "us_ssn", "us_tax_id"],
  "Dirección y Contacto": ["home_address", "home_city", "home_state", "home_postal_code", "home_country", "mailing_address_same", "mailing_address", "phone_primary", "phone_secondary", "phone_work", "email"],
  "Pasaporte": ["passport_type", "passport_number", "passport_book_number", "passport_country", "passport_city_issued", "passport_state_issued", "passport_issue_date", "passport_expiry_date", "has_lost_passport", "lost_passport_details"],
  "Viaje": ["travel_purpose", "travel_specific_plans", "arrival_date", "departure_date", "arrival_city", "stay_address", "who_pays_trip", "payer_details"],
  "Viajes Previos": ["has_previous_us_travel", "previous_travel_dates", "us_drivers_license", "us_drivers_license_details", "has_previous_visa", "previous_visa_number", "previous_visa_issue_date", "visa_same_type", "visa_same_country", "visa_was_refused", "visa_refusal_details", "visa_was_cancelled", "visa_cancellation_details"],
  "Contacto en EE.UU.": ["us_contact_name", "us_contact_organization", "us_contact_relationship", "us_contact_address", "us_contact_city", "us_contact_state", "us_contact_zip", "us_contact_phone", "us_contact_email"],
  "Familia": ["father_surname", "father_given_name", "father_birth_date", "father_in_us", "father_us_status", "mother_surname", "mother_given_name", "mother_birth_date", "mother_in_us", "mother_us_status", "has_relatives_in_us", "relatives_in_us_details", "spouse_surname", "spouse_given_name", "spouse_birth_date", "spouse_nationality"],
  "Trabajo": ["occupation", "employer_name", "employer_address", "employer_city", "employer_phone", "job_title", "job_start_date", "monthly_salary", "job_description", "previous_employer", "previous_employment_details"],
  "Educación": ["education_level", "school_name", "school_address", "course_of_study", "education_start_date", "education_end_date"],
  "Seguridad": ["security_disease", "security_disorder", "security_drug_addict", "security_arrested", "security_arrest_details", "security_controlled_substances", "security_prostitution", "security_money_laundering", "security_trafficking", "security_terrorist", "security_genocide", "security_child_custody", "security_voting", "security_tax_evasion"]
};

const DS160ReviewPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState(["Información Personal"]);
  const [activeComment, setActiveComment] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviewData();
    
    // Auto-refresh every 10 seconds to show client's progress in real-time
    const interval = setInterval(() => {
      fetchReviewData();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [caseId]);

  const fetchReviewData = async () => {
    try {
      const res = await axios.get(`${API}/oficial/cases/${caseId}/ds160-review`);
      setData(res.data);
    } catch (error) {
      console.error(error);
      // Don't show toast on auto-refresh errors
      if (!data) {
        toast.error("Error al cargar los datos del DS-160");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleAddComment = async (fieldId) => {
    if (!commentText.trim()) {
      toast.error("Escribe un comentario");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/oficial/cases/${caseId}/ds160-comments`, {
        field_id: fieldId,
        comment: commentText,
        requires_correction: true
      });
      toast.success("Comentario agregado");
      setActiveComment(null);
      setCommentText("");
      fetchReviewData();
    } catch (error) {
      toast.error("Error al agregar comentario");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (fieldId) => {
    try {
      await axios.delete(`${API}/oficial/cases/${caseId}/ds160-comments/${fieldId}`);
      toast.success("Comentario eliminado");
      fetchReviewData();
    } catch (error) {
      toast.error("Error al eliminar comentario");
    }
  };

  const getCommentForField = (fieldId) => {
    return data?.comments?.find(c => c.field_id === fieldId);
  };

  const formatValue = (value, fieldId) => {
    // Check if field is marked as N/A
    if (isFieldNA(fieldId)) {
      return "No Aplica";
    }
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Sí" : "No";
    return String(value);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-navy-600">No se encontraron datos</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Volver</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { client, ds160_data, detected_risks, progress, pending_corrections } = data;
  const na_fields = ds160_data?.na_fields || {};

  // Check if a field is marked as N/A
  const isFieldNA = (fieldId) => na_fields[fieldId] === true;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-navy-900">Revisión DS-160</h1>
              <p className="text-navy-500">Cliente: {client?.name || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => window.print()}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <FileText className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <div className="px-4 py-2 bg-navy-100 rounded-xl">
              <span className="text-sm text-navy-600">Progreso: </span>
              <span className="font-bold text-navy-900">{progress?.percentage || 0}%</span>
            </div>
            {pending_corrections > 0 && (
              <div className="px-4 py-2 bg-amber-100 rounded-xl">
                <span className="text-sm text-amber-600">{pending_corrections} correcciones pendientes</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Print Header - Only visible when printing */}
        <div className="hidden print:block mb-6 pb-4 border-b-2 border-navy-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">EXPEDIENTE DS-160</h1>
              <p className="text-navy-600">Cliente: {client?.name || "N/A"}</p>
            </div>
            <div className="text-right text-sm">
              <p>Fecha: {new Date().toLocaleDateString('es-ES')}</p>
              <p>Divídelo - Plataforma Migratoria</p>
            </div>
          </div>
        </div>

        {/* Section Progress Overview - Real-time view of client progress */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-navy-900">Progreso en Tiempo Real</h3>
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full animate-pulse">
                Auto-actualiza cada 10s
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchReviewData}
              className="text-blue-600 hover:text-blue-700"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Actualizar ahora
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(FIELD_SECTIONS).map(([sectionName, fields]) => {
              // Count filled fields including N/A
              const filledFields = fields.filter(f => 
                (ds160_data && ds160_data[f] !== undefined && ds160_data[f] !== "" && ds160_data[f] !== null) ||
                isFieldNA(f)
              );
              const filledCount = filledFields.length;
              const totalCount = fields.length;
              const percentage = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;
              const isComplete = filledCount === totalCount;
              const isExpanded = expandedSections.includes(`quick-${sectionName}`);
              
              return (
                <div 
                  key={sectionName}
                  className={`rounded-xl border transition-all cursor-pointer ${
                    isComplete 
                      ? "bg-emerald-50 border-emerald-200 hover:border-emerald-400" 
                      : filledCount > 0 
                        ? "bg-amber-50 border-amber-200 hover:border-amber-400" 
                        : "bg-gray-50 border-gray-200 hover:border-gray-400"
                  }`}
                  onClick={() => setExpandedSections(prev => 
                    prev.includes(`quick-${sectionName}`) 
                      ? prev.filter(s => s !== `quick-${sectionName}`)
                      : [...prev, `quick-${sectionName}`]
                  )}
                >
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-navy-700">{sectionName}</span>
                      <div className="flex items-center gap-1">
                        {isComplete && <Check className="w-4 h-4 text-emerald-600" />}
                        <ChevronDown className={`w-4 h-4 text-navy-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            isComplete ? "bg-emerald-500" : filledCount > 0 ? "bg-amber-500" : "bg-gray-300"
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className={`text-xs font-bold min-w-[40px] text-right ${
                        isComplete ? "text-emerald-600" : filledCount > 0 ? "text-amber-600" : "text-gray-400"
                      }`}>
                        {filledCount}/{totalCount}
                      </span>
                    </div>
                  </div>
                  
                  {/* Expanded view showing actual answers */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-3 bg-white/50 rounded-b-xl max-h-60 overflow-y-auto">
                      <div className="space-y-2">
                        {fields.map(fieldId => {
                          const value = ds160_data?.[fieldId];
                          const hasValue = value !== undefined && value !== "" && value !== null;
                          const isNA = isFieldNA(fieldId);
                          
                          return (
                            <div key={fieldId} className="flex items-start justify-between text-xs">
                              <span className="text-navy-600 flex-1">{FIELD_LABELS[fieldId] || fieldId}:</span>
                              <span className={`ml-2 font-medium text-right max-w-[60%] ${
                                isNA ? "text-slate-500 italic" : hasValue ? "text-navy-900" : "text-gray-400"
                              }`}>
                                {isNA ? "No Aplica" : hasValue ? formatValue(value, fieldId) : "—"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-navy-600">Completada</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-navy-600">En progreso</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                <span className="text-navy-600">Pendiente</span>
              </span>
            </div>
            <span className="text-xs text-blue-500">
              Clic en una sección para ver respuestas
            </span>
          </div>
        </motion.div>

        {/* Risk Alerts */}
        {detected_risks && detected_risks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-6"
            data-testid="oficial-risk-alerts"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-red-900">Alertas de Riesgo Detectadas</h3>
                <p className="text-sm text-red-700">{detected_risks.length} riesgo(s) requieren atención</p>
              </div>
            </div>
            <div className="space-y-3">
              {detected_risks.map((risk, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-xl border ${
                    risk.level === "critical" ? "bg-red-100 border-red-300" :
                    risk.level === "high" ? "bg-orange-100 border-orange-300" :
                    "bg-amber-100 border-amber-300"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                      risk.level === "critical" ? "text-red-600" :
                      risk.level === "high" ? "text-orange-600" :
                      "text-amber-600"
                    }`} />
                    <div>
                      <h4 className="font-semibold text-navy-900">{risk.title}</h4>
                      <p className="text-sm text-navy-600">{risk.message}</p>
                      <p className="text-xs text-navy-500 mt-1">Campo: {FIELD_LABELS[risk.field] || risk.field}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* DS-160 Data Sections */}
        <div className="space-y-4">
          {Object.entries(FIELD_SECTIONS).map(([sectionName, fields]) => {
            const isExpanded = expandedSections.includes(sectionName);
            const sectionComments = fields.filter(f => getCommentForField(f)).length;
            // Count fields that are filled OR marked as N/A
            const filledCount = fields.filter(f => 
              (ds160_data && ds160_data[f] !== undefined && ds160_data[f] !== "" && ds160_data[f] !== null) ||
              isFieldNA(f)
            ).length;

            return (
              <motion.div
                key={sectionName}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-2xl border border-navy-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(sectionName)}
                  className="w-full flex items-center justify-between p-4 hover:bg-navy-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-navy-400" />
                    <div className="text-left">
                      <h3 className="font-semibold text-navy-900">{sectionName}</h3>
                      <p className="text-sm text-navy-500">{filledCount}/{fields.length} campos completados</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {sectionComments > 0 && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                        {sectionComments} comentario(s)
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-navy-400" /> : <ChevronDown className="w-5 h-5 text-navy-400" />}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-navy-100"
                    >
                      <div className="p-4 space-y-3">
                        {fields.map(fieldId => {
                          const value = ds160_data?.[fieldId];
                          const comment = getCommentForField(fieldId);
                          const isAddingComment = activeComment === fieldId;

                          return (
                            <div 
                              key={fieldId}
                              className={`p-4 rounded-xl border transition-colors ${
                                isFieldNA(fieldId)
                                  ? "bg-slate-100 border-slate-300"
                                  : comment 
                                    ? comment.resolved 
                                      ? "bg-emerald-50 border-emerald-200" 
                                      : "bg-amber-50 border-amber-200"
                                    : "bg-navy-50 border-navy-100"
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm text-navy-500">{FIELD_LABELS[fieldId] || fieldId}</p>
                                  <p className={`font-medium mt-1 ${isFieldNA(fieldId) ? "text-slate-500 italic" : "text-navy-900"}`}>
                                    {formatValue(value, fieldId)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {comment ? (
                                    <button
                                      onClick={() => handleDeleteComment(fieldId)}
                                      className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                      title="Eliminar comentario"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => setActiveComment(isAddingComment ? null : fieldId)}
                                      className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                      title="Agregar comentario"
                                    >
                                      <MessageSquare className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Existing Comment */}
                              {comment && (
                                <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                                  <div className="flex items-start gap-2">
                                    <MessageSquare className="w-4 h-4 text-amber-500 mt-0.5" />
                                    <div className="flex-1">
                                      <p className="text-sm text-navy-700">{comment.comment}</p>
                                      <p className="text-xs text-navy-500 mt-1">
                                        Por: {comment.oficial_name} • {new Date(comment.created_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                    {comment.resolved && (
                                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                        Resuelto
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Add Comment Form */}
                              {isAddingComment && (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="mt-3 p-3 bg-white rounded-lg border border-blue-200"
                                >
                                  <textarea
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    placeholder="Escribe tu comentario para el cliente..."
                                    className="w-full p-2 border border-navy-200 rounded-lg text-sm resize-none"
                                    rows={3}
                                  />
                                  <div className="flex justify-end gap-2 mt-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setActiveComment(null);
                                        setCommentText("");
                                      }}
                                    >
                                      <X className="w-4 h-4 mr-1" />
                                      Cancelar
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleAddComment(fieldId)}
                                      disabled={submitting}
                                      className="bg-emerald-500 hover:bg-emerald-600"
                                    >
                                      {submitting ? (
                                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                      ) : (
                                        <Send className="w-4 h-4 mr-1" />
                                      )}
                                      Enviar
                                    </Button>
                                  </div>
                                </motion.div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Summary Footer */}
        <div className="mt-6 p-6 bg-navy-800 rounded-2xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Resumen de Revisión</h3>
              <p className="text-navy-300 text-sm">
                {data.comments?.filter(c => !c.resolved).length || 0} comentarios pendientes de resolución
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400">{progress?.percentage || 0}%</div>
                <p className="text-xs text-navy-400">Completado</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400">{detected_risks?.length || 0}</div>
                <p className="text-xs text-navy-400">Riesgos</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DS160ReviewPage;
